<template>

  <div :class="fadeInClass">
    <!-- Admin/Other Routes -->
    <div v-if="!isKioskView" style="background-color: #fff;">
      <main role="main">
        <router-view></router-view>
      </main>
    </div>
    <template v-else>
      <link rel="stylesheet" href="./kiosk.css">
      <link rel="stylesheet" href="./v3.css">
      <link v-if="config && config.template?.name !== 'default'" rel="stylesheet"
        :href="`./template_${config.template.name}.css`">

      <template v-if="terminalAuthenticated">
        <div :class="{ 'dark-mode': this.config?.darkMode }">
          <TerminalStatusMenu :is-web-socket-connected="isWebSocketConnected" :initialized="initializedAndConnected"
            @attempt-reconnect="attemptReconnect" role="status" aria-live="polite" aria-label="Terminal Status" />
          <div class="full-screen app-container" :class="{ vertical: screenOrientation == 'vertical' }"
            :style="dynamicStyles" role="document">
            <idle-dialog v-if="timeoutActive" @restart="handleIdleTimeout"
              :idle-timeout-seconds="config?.idleTimeoutSeconds || 30" :timeout-active="timeoutActive" />
            <dialog-component />
            <error-component role="alert" aria-live="assertive" aria-labelledby="errorTitle"
              aria-describedby="errorMessage" />
            <LoadingHandler :type="config?.loaderType || 'spinner'" :message="loadingMessage" :show="isLoading" />

            <router-view></router-view>

          </div>
        </div>
      </template>

      <template v-else>
        <RegisterView />
      </template>
    </template>

  </div>


</template>

<script>
import { mapGetters } from "vuex";
import { hexToRGBA } from "@/utils/helpers";
import LoadingHandler from "@/components/features/LoadingHandler.vue";
import ErrorComponent from "@/components/features/ErrorComponent.vue";
import IdleDialog from "@/components/features/IdleDialog.vue";
import DialogComponent from "@/components/features/dialog/DialogComponent.vue";
import TerminalStatusMenu from "@/components/features/TerminalStatusMenu.vue";
import RegisterView from "./views/RegisterView.vue";
import logger from "./utils/logger";
import { inject } from "vue";

export default {
  name: "App",
  components: {
    IdleDialog,
    LoadingHandler,
    ErrorComponent,
    DialogComponent,
    RegisterView,
    TerminalStatusMenu
  },
  data() {
    return {
      fadeInClass: "fade-in"
    };
  },
  setup() {
    const translator = inject('translator');
    return { translator };
  },
  computed: {
    ...mapGetters(["initialized", "isLoading", "isWebSocketConnected", "screenOrientation", "loadingMessage", "processingMessage"]),
    ...mapGetters("location", ["brandColor", "config", "locationName"]),
    ...mapGetters("terminal", ["locationId", "terminalDetails", "terminalAuthenticated"]),
    ...mapGetters("order", ["type", "timeoutActive", "started", "orderTypeId", "confirmed", "appRef"]),
    ...mapGetters("admin", ["isLoggedIn"]),
    initializedAndConnected() {
      return this.initialized && this.isWebSocketConnected;
    },
    idleTimeoutSeconds() {
      return this.$store.state.idleTimeoutSeconds; // Ensure this property is used in your component to react to changes
    },
    adjustedBrandColor() {
      const blackColors = ['#000', '#000000', 'black'];
      return blackColors.includes(this.brandColor.toLowerCase()) ? '#ffffff' : this.brandColor;
    },
    dynamicStyles() {
      return {
        "--brand-color": this.adjustedBrandColor,
        "--primary-color-darker": this.hexToRGBA(this.adjustedBrandColor, 0.8),
        "--bs-primary": this.adjustedBrandColor,
        "--primary-color": this.adjustedBrandColor,
        "--primary-color-transparent": this.hexToRGBA(this.adjustedBrandColor, 0.05),
        "--primary-color-light": this.lightenHexColor(this.adjustedBrandColor, 90),
      };
    },
    isKioskView() {
      return this.$route.name && this.$route.name !== "receipt" && !this.$route.name.includes("admin");
    },
    bannerText() {
      const host = window.location.host;
      if (host === 'kiosk.ngrok.dev') {
        return 'Development Environment';
      } else if (host === 'ordering-staging.skytab.com') {
        return 'Staging Environment';
      } else {
        return null;
      }
    },
    bannerStyle() {
      const host = window.location.host;
      if (host === 'kiosk.ngrok.dev') {
        return {
          backgroundColor: 'red',
          fontSize: '1.5rem',
          color: 'white',
          padding: '0.5rem',
          textAlign: 'center'
        };
      } else if (host === 'ordering-staging.skytab.com') {
        return {
          backgroundColor: 'yellow',
          fontSize: '1rem',
          color: 'black',
          padding: '0.25rem',
          textAlign: 'center'
        };
      } else {
        return {};
      }
    }
  },
  methods: {
    hexToRGBA,
    lightenHexColor(hex, percentage) {
      console.log('lightenHexColor', hex, percentage)
      // Ensure percentage is between 0 and 100
      percentage = Math.min(100, Math.max(0, percentage));

      // Convert hex to RGB
      let r = parseInt(hex.slice(1, 3), 16);
      let g = parseInt(hex.slice(3, 5), 16);
      let b = parseInt(hex.slice(5, 7), 16);

      // Calculate the new color values
      const newR = Math.round(r + (255 - r) * (1 - percentage / 100));
      const newG = Math.round(g + (255 - g) * (1 - percentage / 100));
      const newB = Math.round(b + (255 - b) * (1 - percentage / 100));

      // Convert RGB back to hex
      const toHex = (n) => n.toString(16).padStart(2, '0');
      const newHex = `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;

      return newHex;
    },
    async handleIdleTimeout() {
      if (this.timeoutActive) {
        logger.info('Handling idle timeout, initiating reset.');
        this.reset("idle");
      }
    },
    async reset(source) {
      if (this.started) {
        logger.info(`Resetting order due to ${source}.`);
        await this.$store.dispatch("order/reset", source);
        this.$router.push({ name: "start", params: { locationId: this.$route.params.locationId } });
      }
    },
    async attemptReconnect() {
      logger.info('Attempting to reconnect...');
      if (!navigator.onLine) {
        logger.error('No internet connection.');
        alert('Not connected to the internet.');
        return;
      }

      const pingUrl = `${window.location.protocol}//${window.location.host}/ping.html`;
      try {
        const response = await fetch(pingUrl);

        if (await response.text() == 'OK') {
          logger.info('Reconnection successful, reloading page.');
          //window.location.reload();
        } else {
          logger.error('Kiosk service unavailable after ping check.');
          alert('Kiosk service unavailable.');
        }
      } catch (error) {
        logger.error('Error checking service availability:', error);
        alert('Kiosk service unavailable.');
      }
    },

    checkOrientation() {
      const orientation =
        window.innerWidth > window.innerHeight ? "horizontal" : "vertical";
      this.$store.commit("SET_SCREEN_ORIENTATION", orientation);
    },
    promptRestart() {
      logger.info('Prompting restart due to configuration change.');
      this.$store.dispatch("dialog/openDialog", {
        title: "Configuration Change",
        message: "Settings have changed, and you need to restart your order.",
        onConfirm: () => {
          this.reset('config change');
        }
      });
    }
  },
  mounted() {
    this.checkOrientation();
    window.addEventListener("resize", this.checkOrientation);
    if (this.config?.darkMode) {
      document.documentElement.classList.add('dark-mode');
    }
    setTimeout(() => {
      this.fadeInClass = '';
    }, 1000);
  },
  unmounted() {
    window.removeEventListener("resize", this.checkOrientation);
  },
  watch: {
    'config.darkMode': {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          document.documentElement.classList.add('dark-mode');
        } else {
          document.documentElement.classList.remove('dark-mode');
        }
      }
    },
    "locationName": {
      immediate: true,
      handler(newName) {
        if (this.isKioskView) {
          document.title = `Kiosk - ${newName}`;
        }
      }
    },
    "terminalDetails": {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.$store.dispatch("initWebSocket", { locationId: this.locationId, terminalId: newVal.id });
        }
      }
    },
    "locationId": {
      immediate: true,
      async handler(newLocationId, oldLocationId) {
        if (newLocationId !== oldLocationId) {
          this.$store.dispatch('setTranslator', { translator: this.translator });
          await this.$store.dispatch("initializeLocation", newLocationId);
        }
      },
    },
    'config.active'(newValue) {
      if (!newValue && !this.isLoggedIn) {
        this.$router.push({ name: 'start' }).catch(err => {
          logger.error('Router error:', err);
        });
      }
    },
    'config.orderTypes': {
      handler: function (newVal, oldVal) {
        const orderTypeRef = this.orderTypeId;
        if (this.started && oldVal && newVal !== oldVal && ['start', 'menu', 'checkout'].includes(this.$route.name)) {
          if (orderTypeRef) {
            const updatedOT = newVal?.find(ot => ot.id === orderTypeRef);
            const currentOT = oldVal?.find(ot => ot.id === orderTypeRef);

            if (updatedOT && currentOT) {
              const hasFunctionalChange =
                !updatedOT.active ||
                JSON.stringify(updatedOT.allowedPaymentTypes) !== JSON.stringify(currentOT.allowedPaymentTypes) ||
                JSON.stringify(updatedOT.orderDetails) !== JSON.stringify(currentOT.orderDetails) ||
                updatedOT.name !== currentOT.name ||
                updatedOT.type !== currentOT.type;

              if (hasFunctionalChange) {
                this.promptRestart();
              }
            } else {
              // Order type no longer exists
              this.promptRestart();
            }
          }
        }
      },
      deep: true
    },
    startOver(val) {
      if (val) {
        this.$router.push(`kiosk/start`);
      }
    },
    complete(val) {
      console.log('received complete', val);
      if (val && this.appRef) {
        this.$router.push("complete");
      }
    },
  },
};
</script>

<style>
.full-screen {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

.connection-status {
  position: fixed;
  top: 1rem;
  left: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: grey;
  transition: background-color 0.3s;
}

.connection-status.connected {
  background-color: green;
}

.connection-status.disconnected {
  font-size: 1.5rem;
  padding: 1rem 2rem;
  width: auto;
  height: auto;
  border-radius: 5rem;
  background-color: rbga(255, 0, 0, 0.5);
  color: var(--primary-white);
  box-shadow: var(--box-shadow-medium);
  cursor: pointer;
}

[v-cloak] {
  display: none;
}

.banner {
  width: 100%;
  margin: 1rem;
  padding: 0.5rem;
  text-align: center;
  font-size: 1rem !important;
  border-radius: 1rem;
  color: white;
  position: fixed;
  top: 0;
  z-index: 9999;
  height: auto;
  opacity: 0.5;
}

.fade-in {
  opacity: 0;
  animation: fadeIn 0.5s forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}
</style>
